import { Auth } from '@aws-amplify/auth';

import { ChallengeType } from '../enum';
import { trackSignInError } from '../mixpanel';
import { useAuthStore } from '../store/authStore';
import { authService } from './AuthService';

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

const getCaptchaToken = async (phoneNumber: string) => {
  try {
    const captchaToken = await new Promise<string>((resolve, reject) => {
      if (window.grecaptcha === undefined) {
        trackSignInError(phoneNumber, 'recaptcha_load_error', 'reCAPTCHA script failed to load');
        reject(new Error('reCAPTCHA not loaded'));
        return;
      }

      window.grecaptcha.enterprise.ready(async () => {
        try {
          const token = await window.grecaptcha.enterprise.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY!, {
            action: 'login'
          });
          resolve(token);
        } catch (error) {
          trackSignInError(phoneNumber, 'recaptcha_execution_error', 'reCAPTCHA execution failed');
          reject(error);
        }
      });
    });

    authService.lastGeneratedReCaptchaToken = captchaToken;
    return captchaToken;
  } catch (error) {
    console.error('reCAPTCHA error:', error);
    throw new Error('CAPTCHA_ERROR');
  }
};

export const signIn = async (userIdOrPhoneNumber: string, challengeType: ChallengeType = ChallengeType.OTP, isEmail = false) => {
  const userName = userIdOrPhoneNumber;

  try {
    const captcha = await getCaptchaToken(userName);

    const signInOptions: any = { captcha };
    if (isEmail) {
      signInOptions.challengeType = challengeType;
      signInOptions.email = userName.trim();
    }

    const signInCognitoUser = await Auth.signIn(userName, undefined, signInOptions);

    const challengeAnswerOptions: any = { challengeType };
    if (isEmail) {
      challengeAnswerOptions.email = userName.trim();
    }

    const cognitoUser = await Auth.sendCustomChallengeAnswer(signInCognitoUser, 'PROVIDE_CHALLENGE_TYPE', challengeAnswerOptions);

    return {
      cognitoUser,
      challenge: cognitoUser.challengeParam.challenge,
      resendIn: parseInt(cognitoUser.challengeParam.resendIn)
    };
  } catch (error) {
    if (error instanceof Error && error.message === 'CAPTCHA_ERROR') {
      throw new Error('CAPTCHA_ERROR');
    }
    throw error;
  }
};

export const verifyCode = async (code: string, cognitoUser: any, challengeType: ChallengeType = ChallengeType.OTP, isEmail = false) => {
  try {
    const verifyOptions: any = { challengeType };
    if (isEmail) {
      verifyOptions.challengeType = ChallengeType.EMAIL_OTP;
    }

    await Auth.sendCustomChallengeAnswer(cognitoUser, code.trim(), verifyOptions);
    const { setUserId } = useAuthStore.getState();
    const authenticatedUser = await Auth.currentAuthenticatedUser();
    setUserId(authenticatedUser.attributes.sub);

    return authenticatedUser;
  } catch {
    throw new Error('INVALID_CODE');
  }
};

export const resendCode = async (cognitoUser: any, isEmail = false) => {
  const challengeType = isEmail ? ChallengeType.EMAIL_RESEND_OTP : ChallengeType.RESEND_OTP;
  const response = await Auth.sendCustomChallengeAnswer(cognitoUser, challengeType, { challengeType });
  return {
    resendIn: parseInt(response.challengeParam.resendIn)
  };
};

export const isEmailValid = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

export const getUserAttributes = (user: any) =>
  new Promise<Record<string, string>>((resolve, reject) => {
    user.getUserAttributes((error, result) => {
      if (error) {
        return reject(error);
      }
      if (!result) {
        return resolve({});
      }
      resolve(
        result.reduce((res, attr) => {
          res[attr.getName()] = attr.getValue();
          return res;
        }, {} as Record<string, string>)
      );
    });
  });